import { useMutation, useQuery, useQueryClient } from "react-query";

import {
  createApiToken,
  createServiceAccount,
  deleteServiceAccount,
  listApiTokens,
  listPermissionCatalog,
  listServiceAccounts,
  revokeApiToken,
  updateServiceAccount,
} from "./api";
import type {
  CreateApiTokenInput,
  CreateServiceAccountInput,
  UpdateServiceAccountInput,
} from "./types";

const serviceAccountsKey = ["serviceAccounts"] as const;
const apiTokensKey = (serviceAccountId: string) =>
  ["serviceAccounts", serviceAccountId, "tokens"] as const;
const permissionCatalogKey = ["serviceAccounts", "permissionCatalog"] as const;

export function useServiceAccounts() {
  return useQuery(serviceAccountsKey, listServiceAccounts, {
    staleTime: 30_000,
  });
}

// enabled lets a caller defer the fetch until it is actually needed. The
// permission-simulation modal passes `enabled: open` so the catalog is fetched
// when the modal opens rather than at page load — right after a masquerade
// reload the env locker still holds the employee's previous env (sessionStorage
// survives the reload until account metadata loads and resets it), so a
// page-load fetch would send a stale X-Env-ID, 401 against the customer account,
// and stick because of staleTime: Infinity until a manual refresh.
export function usePermissionCatalog(options?: { enabled?: boolean }) {
  return useQuery(permissionCatalogKey, listPermissionCatalog, {
    staleTime: Infinity,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateServiceAccount() {
  const queryClient = useQueryClient();
  return useMutation(
    (input: CreateServiceAccountInput) => createServiceAccount(input),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(serviceAccountsKey);
      },
    },
  );
}

export function useUpdateServiceAccount() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, input }: { id: string; input: UpdateServiceAccountInput }) =>
      updateServiceAccount(id, input),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(serviceAccountsKey);
      },
    },
  );
}

export function useDeleteServiceAccount() {
  const queryClient = useQueryClient();
  return useMutation((id: string) => deleteServiceAccount(id), {
    onSuccess: () => {
      void queryClient.invalidateQueries(serviceAccountsKey);
    },
  });
}

export function useApiTokens(serviceAccountId: string | null) {
  return useQuery(
    apiTokensKey(serviceAccountId ?? ""),
    () => listApiTokens(serviceAccountId as string),
    { enabled: !!serviceAccountId, staleTime: 30_000 },
  );
}

export function useCreateApiToken(serviceAccountId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (input: CreateApiTokenInput) => createApiToken(serviceAccountId, input),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(apiTokensKey(serviceAccountId));
        void queryClient.invalidateQueries(serviceAccountsKey);
      },
    },
  );
}

export function useRevokeApiToken(serviceAccountId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (apiTokenId: string) => revokeApiToken(serviceAccountId, apiTokenId),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(apiTokensKey(serviceAccountId));
      },
    },
  );
}
